import { Modal, ModalButtons, ModalProps } from 'ui/Modal'
import { Button } from 'ui/components/Button'
import { useGetApplicableOfferingsForCouponCode } from 'api/CouponCodes'
import { MiniList } from 'ui/components/MiniList'
import { ResourceListItem } from 'ui/components/ResourceListItem'
import { Badge } from 'ui/components/Badge'
import { sentenceCase } from 'change-case'
import {
  TYPE_APPOINTMENT,
  TYPE_AREA_BOOKING,
  TYPE_PACKAGE,
  TYPE_PRODUCT,
  TYPE_SESSION,
} from 'constants/offeringTypes'
import { useHistory } from 'react-router-dom'
import { formatMoney } from 'ui/components/Money'
import { useCurrentSite } from 'contexts/SiteConfig'
import { useGetCoupon } from 'api/Coupon'
import { SETTINGS_VIEW } from 'constants/permissions'
import { useCurrentUserCanForCurrentSite } from 'hooks/UseCurrentUserCan'
import { Trans, useTranslation } from 'react-i18next'
import { FC } from 'react'

interface Props extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  code?: string | null
  couponId?: string | null
}

export const ApplicableOfferingsForCouponModal: FC<Props> = ({
  onClose,
  couponId,
  code,
  isOpen = false,
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { currency: siteCurrency } = useCurrentSite()
  const can = useCurrentUserCanForCurrentSite()
  const userCanViewSettings = can(SETTINGS_VIEW)

  const {
    data: { data: codeDiscount = {} } = {},
    isLoading: isLoadingCode,
    isError: isCodeError,
  } = useGetApplicableOfferingsForCouponCode(code!, {
    enabled: code != null,
  })

  const {
    data: { data: { offering_discounts: offeringDiscounts = [] } = {} } = {},
    isLoading: isLoadingCoupon,
    isError: isCouponError,
  } = useGetCoupon(couponId!, {
    enabled: couponId != null,
  })

  const isLoading = isLoadingCode || isLoadingCoupon
  const isError = isCodeError || isCouponError

  const {
    offerings = [],
    discount_type: discountType = '',
    percentage = 0,
    fixed_amount: fixedAmount = 0,
  } = offeringDiscounts[0] ?? codeDiscount

  const onOfferingClick = ({ offering_id: id, offering_type: type }) => {
    if (!userCanViewSettings) {
      return
    }

    switch (type) {
      case TYPE_APPOINTMENT:
        return history.push(`/settings/offerings/appointment-types/${id}`)

      case TYPE_AREA_BOOKING:
        return history.push(`/settings/offerings/area-booking-types/${id}`)

      case TYPE_PRODUCT:
        return history.push(`/settings/offerings/retail-products/${id}`)

      case TYPE_SESSION:
        return history.push(`/settings/offerings/session-types/${id}`)

      case TYPE_PACKAGE:
        return history.push(`/settings/offerings/package-types/${id}`)
    }
  }

  return (
    <>
      <Modal
        size="sm"
        title={t('applicable_offerings_modal.title')}
        isOpen={isOpen}
        onClose={onClose}
      >
        <div className="my-4 text-sm text-gray-500">
          {discountType === 'percentage' ? (
            <Trans
              i18nKey="applicable_offerings_modal.description_percentage"
              values={{ discount: `${percentage}%` }}
              components={[<strong className="font-medium text-gray-700" />]}
            />
          ) : (
            <Trans
              i18nKey="applicable_offerings_modal.description_fixed"
              values={{
                amount: formatMoney({
                  amount: fixedAmount,
                  currency: siteCurrency,
                }),
              }}
              components={[<strong className="font-medium text-gray-700" />]}
            />
          )}
        </div>

        <MiniList
          isLoading={isLoading}
          isError={isError}
          emptyMessage={t('applicable_offerings_modal.empty_message')}
          rows={offerings.map((offering, i) => (
            <ResourceListItem
              key={i}
              title={offering.offering_name}
              badge={<Badge variant="info" label={sentenceCase(offering.offering_type)} />}
              onClick={() => onOfferingClick(offering)}
              showDisclosureArrow={userCanViewSettings}
              compact
            />
          ))}
        />
        <ModalButtons>
          <Button label={t('modal_button.close')} onClick={() => onClose(false)} />
        </ModalButtons>
      </Modal>
    </>
  )
}
